import React from 'react';
import { Button } from '@/components/ui/button';
import { Download, Wifi, Zap, X } from 'lucide-react';
import { usePWA } from '@/hooks/usePWA';
import { toast } from 'sonner';
import { IOSInstallGuide } from './IOSInstallGuide';

export const PWAInstallPrompt: React.FC = () => {
  const { isInstallable, isOnline, install } = usePWA();
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [showIOSGuide, setShowIOSGuide] = React.useState(false);
  const [visits, setVisits] = React.useState(0);
  const [interactionTime, setInteractionTime] = React.useState(0);

  React.useEffect(() => {
    // Detectar iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Contar visitas
    const visitCount = parseInt(localStorage.getItem('pwa-visit-count') || '0');
    const newVisitCount = visitCount + 1;
    localStorage.setItem('pwa-visit-count', newVisitCount.toString());
    setVisits(newVisitCount);

    // Medir tiempo de interacción
    const startTime = Date.now();
    const interval = setInterval(() => {
      setInteractionTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    // Mostrar prompt con estrategia inteligente
    const lastDismissed = localStorage.getItem('pwa-install-dismissed');
    const daysSinceDismissed = lastDismissed 
      ? (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24)
      : 999;

    // Mostrar después de 3 visitas o 45 segundos de interacción (lo que ocurra primero)
    if ((isInstallable || (isIOS && isSafari)) && daysSinceDismissed > 1) {
      const timer = setTimeout(() => {
        if (newVisitCount >= 3 || interactionTime >= 45) {
          // Trackear que se mostró el prompt
          const shownCount = parseInt(localStorage.getItem('pwa-prompt-shown') || '0');
          localStorage.setItem('pwa-prompt-shown', (shownCount + 1).toString());
          console.log('📊 PWA Prompt mostrado -', shownCount + 1, 'veces');
          
          if (isIOS && isSafari && !isInstallable) {
            setShowIOSGuide(true);
          } else {
            setShowPrompt(true);
          }
        }
      }, Math.min(45000, newVisitCount < 3 ? 60000 : 20000));

      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }

    return () => clearInterval(interval);
  }, [isInstallable, interactionTime]);

  const handleInstall = async () => {
    try {
      await install();
      setShowPrompt(false);
      toast.success('¡Menú instalado! Ahora funciona offline 🚀');
      
      // Analytics: registrar instalación exitosa
      localStorage.setItem('pwa-installed', 'true');
      localStorage.setItem('pwa-install-date', Date.now().toString());
      localStorage.setItem('pwa-install-accepted', '1');
      
      // Trackear en localStorage para analytics
      const installCount = parseInt(localStorage.getItem('pwa-prompt-shown') || '0');
      if (installCount > 0) {
        console.log('📊 PWA instalada - Conversión:', (1/installCount * 100).toFixed(1) + '%');
      }
    } catch (error) {
      toast.error('No se pudo instalar la app');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    
    // Analytics: registrar rechazo
    const dismissCount = parseInt(localStorage.getItem('pwa-dismiss-count') || '0');
    localStorage.setItem('pwa-dismiss-count', (dismissCount + 1).toString());
    
    // Trackear ratio de conversión
    const promptCount = parseInt(localStorage.getItem('pwa-prompt-shown') || '0');
    if (promptCount > 0) {
      console.log('📊 PWA rechazada -', dismissCount + 1, 'veces de', promptCount, 'prompts');
    }
  };

  if (showIOSGuide) {
    return <IOSInstallGuide onClose={handleDismiss} />;
  }

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-4 right-4 z-50 max-w-sm mx-auto">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-4 shadow-2xl border border-orange-400/20 animate-fade-in">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="bg-white/20 p-2 rounded-xl">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div className="text-white font-semibold">¡Instalar Menú!</div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-white/90 text-sm mb-4 leading-relaxed">
          Instala este menú en tu teléfono para:
        </p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-white/90 text-sm">
            <Wifi className="w-4 h-4" />
            <span>Funcionar sin internet</span>
          </div>
          <div className="flex items-center gap-2 text-white/90 text-sm">
            <Zap className="w-4 h-4" />
            <span>Cargar instantáneamente</span>
          </div>
          <div className="flex items-center gap-2 text-white/90 text-sm">
            <Download className="w-4 h-4" />
            <span>Acceso directo en pantalla</span>
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={handleInstall}
            className="flex-1 bg-white text-orange-500 hover:bg-white/90 font-semibold"
          >
            Instalar
          </Button>
          <Button 
            onClick={handleDismiss}
            variant="outline"
            className="text-white border-white/30 hover:bg-white/10"
          >
            Ahora no
          </Button>
        </div>
      </div>
    </div>
  );
};